import { useState } from 'react';
import { Box, Text, VStack } from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { useTranslation } from '../../../contexts/TranslationContext';
import { useLeadActivities } from '../../../hooks/useLeads';
import { colors } from '../../../theme/colors';
import { ActivityTimeline } from './ActivityTimeline';

const PAGE_SIZE = 25;

interface LeadActivitySectionProps {
  leadId: string;
}

/**
 * Owns the lead timeline query so the automation toggle filters at the source.
 *
 * The lead detail screen used to render the activities embedded in the lead
 * payload — the 20 most recent rows, unfiltered — and hide automation on the
 * client. A lead whose 20 newest rows were all automation then showed an empty
 * timeline while its human history sat unreachable underneath the cap
 * (FLOSEAI-462). Driving `includeAutomation` into the query makes the toggle
 * change what is fetched rather than what is hidden, and lifts the cap.
 */
export function LeadActivitySection({ leadId }: Readonly<LeadActivitySectionProps>) {
  const { t } = useTranslation();
  const [showAutomation, setShowAutomation] = useState(false);

  const { activities, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useLeadActivities(leadId, PAGE_SIZE, showAutomation);

  if (isLoading) {
    return (
      <Box padding="lg" alignItems="center">
        <Text variant="caption" color={colors.text.tertiary}>
          {t('common.loading')}
        </Text>
      </Box>
    );
  }

  return (
    <VStack spacing="sm">
      <ActivityTimeline
        activities={activities}
        showAutomation={showAutomation}
        onToggleAutomation={setShowAutomation}
        prefiltered
      />

      {hasNextPage && (
        <Button
          variant="ghost"
          size="sm"
          // Guarded here as well as disabled: the disabled prop is a UI
          // affordance, and a double-tap that slips past it would queue a
          // redundant page request.
          onPress={() => {
            if (!isFetchingNextPage) fetchNextPage();
          }}
          disabled={isFetchingNextPage}
          accessibilityRole="button"
          accessibilityLabel={t('leads.activityTimeline.loadMore')}
          accessibilityState={{ disabled: isFetchingNextPage }}
        >
          {t('leads.activityTimeline.loadMore')}
        </Button>
      )}
    </VStack>
  );
}
